import { useState, useEffect } from 'react'
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import dayjs from 'dayjs'
import { styled } from '@mui/material/styles'

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isStart' && prop !== 'isEnd' && prop !== 'isInRange',
})(({ theme, isSelected, isStart, isEnd, isInRange }) => ({
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: theme.typography.fontWeightBold,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isStart && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: theme.typography.fontWeightBold,
  }),
  ...(isEnd && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: theme.typography.fontWeightBold,
  }),
  ...(isInRange && {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: 0,
  }),
}))

export default function DateRangeCalendarPicker({ startDate, endDate, onStartDateChange, onEndDateChange, maxDate }) {
  const [hoveredDate, setHoveredDate] = useState(null)
  const [selectingStart, setSelectingStart] = useState(!startDate || (startDate && endDate))
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Update selectingStart when dates change externally
  useEffect(() => {
    setSelectingStart(!startDate || (startDate && endDate))
  }, [startDate, endDate])

  const handleDateClick = (date) => {
    if (selectingStart || !startDate || (startDate && endDate)) {
      // Start new selection
      onStartDateChange(date)
      onEndDateChange(null)
      setSelectingStart(false)
    } else if (startDate && !endDate) {
      // Complete the selection
      if (date.isBefore(startDate)) {
        // If clicked date is before start, swap them
        onEndDateChange(startDate)
        onStartDateChange(date)
      } else {
        onEndDateChange(date)
      }
      setSelectingStart(true)
    }
  }

  const isDateInRange = (date) => {
    if (!startDate) return false
    if (endDate) {
      return date.isAfter(startDate) && date.isBefore(endDate)
    }
    if (hoveredDate && hoveredDate.isAfter(startDate)) {
      return date.isAfter(startDate) && date.isBefore(hoveredDate)
    }
    return false
  }

  const isDateStart = (date) => {
    return startDate && date.isSame(startDate, 'day')
  }

  const isDateEnd = (date) => {
    return endDate && date.isSame(endDate, 'day')
  }

  const isDateSelected = (date) => {
    return isDateStart(date) || isDateEnd(date)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box
          sx={{
            '& .MuiDateCalendar-root': {
              width: '100%',
              minHeight: { xs: '260px', sm: '280px' },
            },
            '& .MuiPickersCalendarHeader-root': {
              paddingLeft: 1.5,
              paddingRight: 1.5,
              paddingTop: 1,
              paddingBottom: 1,
            },
            '& .MuiDayCalendar-weekContainer': {
              margin: '2px 0',
            },
            '& .MuiPickersDay-root': {
              fontSize: { xs: '12px', sm: '13px' },
              width: { xs: '32px', sm: '36px' },
              height: { xs: '32px', sm: '36px' },
            },
          }}
        >
          <DateCalendar
            value={startDate || endDate || dayjs()}
            onChange={(newValue) => {
              if (newValue) {
                handleDateClick(newValue)
              }
            }}
            maxDate={maxDate || dayjs()}
            slots={{
              day: CustomPickersDay,
            }}
            slotProps={{
              day: (ownerState) => {
                const date = ownerState.day
                return {
                  isSelected: isDateSelected(date),
                  isStart: isDateStart(date),
                  isEnd: isDateEnd(date),
                  isInRange: isDateInRange(date),
                  onMouseEnter: () => {
                    if (startDate && !endDate) {
                      setHoveredDate(date)
                    }
                  },
                  onMouseLeave: () => {
                    setHoveredDate(null)
                  },
                }
              },
            }}
          />
        </Box>
        <Box 
          sx={{ 
            mt: 1.5, 
            p: 1.5, 
            backgroundColor: 'rgba(116, 80, 170, 0.05)', 
            borderRadius: '8px' 
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              mb: 0.5, 
              fontWeight: 500,
              fontSize: '12px',
              display: 'block',
            }}
          >
            Selected Range:
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '11px', display: 'block' }}
          >
            {startDate ? `Start: ${startDate.format('MMM DD, YYYY')}` : 'No start date selected'}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '11px', display: 'block' }}
          >
            {endDate ? `End: ${endDate.format('MMM DD, YYYY')}` : 'No end date selected'}
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}

